import React, { useEffect, useState } from 'react';
import { Avatar } from '@mui/material';
import { usersService } from '../services/usersService';

/**
 * Avatar do usuário logado (RN-013b): mostra a MINIATURA da foto do perfil (gerada no upload,
 * RN-013a) e, sem foto, a inicial do nome. A miniatura é buscada uma vez por sessão e
 * compartilhada entre o cabeçalho e o menu lateral — cada instância não refaz a chamada.
 * Ao trocar a foto no Meu perfil, `UserAvatar.refresh()` invalida o cache.
 */
let thumbPromise: Promise<string | null> | null = null;
const listeners = new Set<() => void>();

const loadThumb = (): Promise<string | null> => {
  if (!thumbPromise) {
    thumbPromise = usersService.getMyPhoto(true)
      .then((blob) => (blob ? URL.createObjectURL(blob) : null))
      .catch(() => null);
  }
  return thumbPromise;
};

export const refreshUserAvatar = (): void => {
  thumbPromise = null;
  listeners.forEach((l) => l());
};

interface Props {
  /** Nome (ou email) para a inicial quando não há foto */
  label: string;
  size?: number;
  onClick?: () => void;
  title?: string;
}

const UserAvatar: React.FC<Props> = ({ label, size = 36, onClick, title }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const run = () => { loadThumb().then((url) => { if (alive) setSrc(url); }); };
    run();
    listeners.add(run);
    return () => { alive = false; listeners.delete(run); };
  }, []);

  const initial = (label || '?').trim().charAt(0).toUpperCase();
  return (
    <Avatar
      src={src ?? undefined}
      alt={label}
      title={title}
      onClick={onClick}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.45,
        fontWeight: 600,
        bgcolor: '#03B4C6',
        color: '#fff',
        cursor: onClick ? 'pointer' : 'default',
        border: '2px solid #fff',
        boxShadow: '0 0 0 1px #e9ecef',
      }}
    >
      {initial}
    </Avatar>
  );
};

export default UserAvatar;
